import firebase, { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";
import { uploadMedia } from "../media/cloudflareMedia";
import {
  BoardArea,
  BoardAttachment,
  BoardCard,
  BoardCardType,
  BoardComment,
  BoardPriority,
  BoardStageDef,
  DEFAULT_BOARD_STAGES,
} from "../../types/Board";

// Client access to the global issue board at Board/global. Every admin can
// read the board, add cards, comment, and attach screenshots; moving cards,
// editing stages, and deleting are operator-only in the UI (isBoardOperator).
// Server-side hardening would need a firestore.rules rework (admin identity
// lives in RTDB, not Firestore), so like the rest of the admin collections the
// gate is client-side + MCP-side for now.

const BOARD_DOC_ID = "global";

const boardRef = () => firestore.collection(DB_PATH.BOARD).doc(BOARD_DOC_ID);

const cardsCollection = () => boardRef().collection(DB_PATH.BOARD_CARDS);

const commentsCollection = (cardId: string) =>
  cardsCollection().doc(cardId).collection(DB_PATH.BOARD_COMMENTS);

const nowIso = () => new Date().toISOString();

const sanitizeStage = (raw: any): BoardStageDef | null => {
  if (!raw || typeof raw.key !== "string" || !raw.key) return null;
  return {
    key: raw.key,
    label: typeof raw.label === "string" && raw.label ? raw.label : raw.key,
    ...(raw.tone ? { tone: raw.tone } : {}),
  };
};

const sanitizeCard = (id: string, data: any): BoardCard => ({
  id,
  type: (data?.type || "bug") as BoardCardType,
  area: (data?.area || "customer_app") as BoardArea,
  title: typeof data?.title === "string" ? data.title : "",
  description: typeof data?.description === "string" ? data.description : "",
  status: typeof data?.status === "string" ? data.status : "backlog",
  priority: (data?.priority || "medium") as BoardPriority,
  position: typeof data?.position === "number" ? data.position : 0,
  createdBy: {
    uid: data?.createdBy?.uid || "",
    fullName: data?.createdBy?.fullName || "Unknown",
  },
  creatorCommunityId:
    typeof data?.creatorCommunityId === "string" ? data.creatorCommunityId : "",
  creatorCommunityName:
    typeof data?.creatorCommunityName === "string"
      ? data.creatorCommunityName
      : "",
  attachments: Array.isArray(data?.attachments)
    ? data.attachments.filter((a: any) => a && a.url)
    : [],
  ...(data?.sentryIssueId
    ? {
        sentryIssueId: data.sentryIssueId,
        sentryUrl: data.sentryUrl || "",
        sentryEnvironment: data.sentryEnvironment || "",
        sentryRelease: data.sentryRelease ?? null,
        sentryDist: data.sentryDist ?? null,
        sentryPlatform: data.sentryPlatform ?? null,
        sentryBrand: data.sentryBrand ?? null,
        sentryDevice: data.sentryDevice ?? null,
        sentryFingerprint: data.sentryFingerprint ?? null,
        sentryLastSeenAt: data.sentryLastSeenAt || "",
        sentryEventCount:
          typeof data.sentryEventCount === "number"
            ? data.sentryEventCount
            : 0,
        sentryDiagnostics:
          data.sentryDiagnostics &&
          typeof data.sentryDiagnostics === "object"
            ? data.sentryDiagnostics
            : undefined,
      }
    : {}),
  createdAt: typeof data?.createdAt === "string" ? data.createdAt : "",
  updatedAt: typeof data?.updatedAt === "string" ? data.updatedAt : "",
});

const sanitizeComment = (id: string, data: any): BoardComment => ({
  id,
  text: typeof data?.text === "string" ? data.text : "",
  author: {
    uid: data?.author?.uid || "",
    fullName: data?.author?.fullName || "Unknown",
  },
  authorCommunityId:
    typeof data?.authorCommunityId === "string" ? data.authorCommunityId : "",
  createdAt: typeof data?.createdAt === "string" ? data.createdAt : "",
});

// ─── stages ──────────────────────────────────────────────────────────────────

export const listenToBoardStages = (
  onChange: (stages: BoardStageDef[]) => void,
  onError?: (err: Error) => void
): (() => void) =>
  boardRef().onSnapshot(
    (snap) => {
      const raw = snap.exists ? (snap.data() as any)?.stages : null;
      const stages = Array.isArray(raw)
        ? (raw.map(sanitizeStage).filter(Boolean) as BoardStageDef[])
        : [];
      onChange(stages.length ? stages : DEFAULT_BOARD_STAGES);
    },
    (err) => {
      console.error("Error listening to board stages:", err);
      onError?.(err);
    }
  );

export const setBoardStages = async (
  stages: BoardStageDef[]
): Promise<void> => {
  const keys = stages.map((s) => s.key);
  const dupe = keys.find((k, i) => keys.indexOf(k) !== i);
  if (dupe) throw new Error(`Duplicate stage key "${dupe}".`);
  if (!stages.length) throw new Error("Keep at least one stage.");
  await boardRef().set({ stages, updatedAt: nowIso() }, { merge: true });
};

// ─── cards ───────────────────────────────────────────────────────────────────

export const listenToBoardCards = (
  onChange: (cards: BoardCard[]) => void,
  onError?: (err: Error) => void
): (() => void) =>
  cardsCollection()
    .orderBy("position", "asc")
    .onSnapshot(
      (snap) => onChange(snap.docs.map((d) => sanitizeCard(d.id, d.data()))),
      (err) => {
        console.error("Error listening to board cards:", err);
        onError?.(err);
      }
    );

export type BoardCardInput = {
  type: BoardCardType;
  area: BoardArea;
  title: string;
  description?: string;
  priority?: BoardPriority;
  status?: string;
};

export type BoardCreator = {
  uid: string;
  fullName: string;
  communityId: string;
  communityName: string;
};

export const createBoardCard = async (
  input: BoardCardInput,
  creator: BoardCreator
): Promise<string> => {
  const title = (input.title || "").trim();
  if (!title) throw new Error("Title is required.");
  const now = nowIso();
  const ref = await cardsCollection().add({
    type: input.type,
    area: input.area,
    title,
    description: (input.description || "").trim(),
    status: input.status || DEFAULT_BOARD_STAGES[0].key,
    priority: input.priority || "medium",
    position: Date.now(),
    createdBy: { uid: creator.uid, fullName: creator.fullName },
    creatorCommunityId: creator.communityId,
    creatorCommunityName: creator.communityName,
    attachments: [],
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
};

export const updateBoardCard = async (
  id: string,
  patch: Partial<
    Pick<
      BoardCard,
      | "type"
      | "area"
      | "title"
      | "description"
      | "priority"
      | "status"
      | "position"
    >
  >
): Promise<void> => {
  const update: Record<string, any> = { updatedAt: nowIso() };
  if (typeof patch.title === "string") update.title = patch.title.trim();
  if (typeof patch.description === "string")
    update.description = patch.description.trim();
  if (patch.type) update.type = patch.type;
  if (patch.area) update.area = patch.area;
  if (patch.priority) update.priority = patch.priority;
  if (typeof patch.status === "string") update.status = patch.status;
  if (typeof patch.position === "number") update.position = patch.position;
  await cardsCollection().doc(id).set(update, { merge: true });
};

export const moveBoardCard = (
  id: string,
  status: string,
  position: number
): Promise<void> => updateBoardCard(id, { status, position });

// Comments are left orphaned on delete (no recursive client delete in the v8
// SDK); acceptable for an internal tracker.
export const deleteBoardCard = async (id: string): Promise<void> => {
  await cardsCollection().doc(id).delete();
};

// ─── comments ────────────────────────────────────────────────────────────────

export const listenToBoardComments = (
  cardId: string,
  onChange: (comments: BoardComment[]) => void,
  onError?: (err: Error) => void
): (() => void) =>
  commentsCollection(cardId)
    .orderBy("createdAt", "asc")
    .onSnapshot(
      (snap) => onChange(snap.docs.map((d) => sanitizeComment(d.id, d.data()))),
      (err) => {
        console.error("Error listening to board comments:", err);
        onError?.(err);
      }
    );

export const addBoardComment = async (
  cardId: string,
  text: string,
  author: { uid: string; fullName: string; communityId: string }
): Promise<void> => {
  const trimmed = (text || "").trim();
  if (!trimmed) return;
  await commentsCollection(cardId).add({
    text: trimmed,
    author: { uid: author.uid, fullName: author.fullName },
    authorCommunityId: author.communityId,
    createdAt: nowIso(),
  });
  await cardsCollection()
    .doc(cardId)
    .set({ updatedAt: nowIso() }, { merge: true });
};

// ─── attachments ─────────────────────────────────────────────────────────────

const guessExtension = (file: File): string => {
  if (file.type === "image/png") return "png";
  if (file.type === "image/jpeg") return "jpg";
  if (file.type === "image/webp") return "webp";
  const dot = file.name.lastIndexOf(".");
  if (dot >= 0) return file.name.slice(dot + 1).toLowerCase();
  return "png";
};

export const uploadBoardAttachment = async (
  cardId: string,
  file: File
): Promise<BoardAttachment> => {
  if (!cardId) throw new Error("cardId is required.");
  if (!file) throw new Error("file is required.");
  const id = firestore.collection("_").doc().id;
  const ext = guessExtension(file);
  const path = `Board/${BOARD_DOC_ID}/Cards/${cardId}/${id}.${ext}`;
  const uploaded = await uploadMedia({
    body: file,
    fileName: file.name,
    firebasePath: path,
  });
  const url = uploaded.url;
  const attachment: BoardAttachment = {
    id,
    url,
    path,
    name: file.name || `${id}.${ext}`,
    createdAt: nowIso(),
  };
  await cardsCollection()
    .doc(cardId)
    .set(
      {
        attachments: firebase.firestore.FieldValue.arrayUnion(attachment),
        updatedAt: nowIso(),
      },
      { merge: true }
    );
  return attachment;
};
